import type {
  OfflineModeBroadcastPrepareErrorCode,
  OfflineModeBroadcastRefusalCode,
} from 'redux-stores/offlineMode';

export type ConfirmationCopy = {
  title: string;
  paragraphs: readonly string[];
  notice?: string;
  confirmLabel: string;
  confirmInFlightLabel: string;
  cancelLabel: string;
};

export const FALLBACK_ORG_NAME = 'this organization';

export const orgLabel = (organizationName?: string): string =>
  organizationName?.trim() || FALLBACK_ORG_NAME;

export const offlineOrgLabel = (organizationName?: string): string =>
  `${orgLabel(organizationName)} (Offline)`;

function activateCopy(org: string, offline: string): ConfirmationCopy {
  return {
    title: 'Activate Offline Mode',
    paragraphs: [
      `Activating Offline Mode switches every user in the organization to ${offline}. ` +
        'Conversations started there stay there.',
      'The cloud org remains available for reference and is not disabled. Messages sent in ' +
        `${org} will not reach users who have switched.`,
      'The organization will not return to the cloud org automatically when connectivity is ' +
        'restored. Returning is a separate action taken here.',
    ],
    notice:
      'Every connected device is told automatically and shows a banner within about a minute. ' +
      `Nothing interrupts anyone — send a broadcast message in ${offline} after activating to ` +
      'reach people now.',
    confirmLabel: 'Activate Offline Mode',
    confirmInFlightLabel: 'Activating…',
    cancelLabel: 'Cancel',
  };
}

function returnCopy(org: string, offline: string): ConfirmationCopy {
  return {
    title: 'Return to Cloud Mode',
    paragraphs: [
      `Returning to Cloud Mode makes ${org} the operating org again and disables ${offline} in ` +
        `the org switcher. Conversations started in ${offline} stay there and are not merged ` +
        `into ${org}. Every connected device is told automatically; send a broadcast message in ` +
        `${org} to bring people back now.`,
    ],
    confirmLabel: 'Return to Cloud Mode',
    confirmInFlightLabel: 'Returning…',
    cancelLabel: 'Cancel',
  };
}

export function confirmationCopy(
  direction: 'activate' | 'return',
  organizationName?: string
): ConfirmationCopy {
  const org = orgLabel(organizationName);
  const offline = offlineOrgLabel(organizationName);
  return direction === 'activate' ? activateCopy(org, offline) : returnCopy(org, offline);
}

export const PANEL_TITLE = 'Offline Mode';

export const pageDescription = (organizationName?: string): string =>
  `Switch the organization to ${offlineOrgLabel(organizationName)} during a ` +
  'degraded-connectivity event, and bring it back afterward.';

export const DORMANT = {
  status: 'Offline Mode is not active.',
  availability:
    'The Offline Mode organization is visible to everyone in the organization switcher and ' +
    'disabled. Selecting it explains why it is unavailable; nobody can send a message there ' +
    'until Offline Mode is active.',
  activateHint:
    'Activating turns the whole organization over. You will be asked to confirm, and the ' +
    'confirmation says exactly what changes.',
  lastActivationLabel: 'Last activation',
  sendReturnBroadcast: 'Send Return Broadcast',
};

export const ACTIVATION_FACTS_HEADING = 'What Activation Does';

export const ACTIVATION_FACTS: readonly string[] = [
  'Enables the Offline Mode organization for the people in this organization and marks it the ' +
    'operating org.',
  'Leaves the cloud organization readable. Message history stays available; messages sent there ' +
    'will not reach users who have switched.',
  'Tells every connected device automatically — a banner appears within about a minute. Nothing ' +
    'interrupts anyone; use Send Broadcast Message to reach people now.',
  'Does not change Care Conduit alert routing. Alert failover is automatic and heals on its own.',
];

export const ACTIVE = {
  status: 'Offline Mode is active',
  body: (offlineOrg: string): string =>
    `Every user belongs in ${offlineOrg}. The cloud org stays available for reference and will ` +
    'not switch back on its own.',
  sendBroadcast: 'Send Broadcast Message',
};

export const NOT_AUTHORIZED = {
  title: 'You do not have permission to administer Offline Mode',
  body:
    'Changing the organization between Cloud Mode and Offline Mode requires the ' +
    'offline_mode_activation permission on this deployment. Your account does not hold it, so ' +
    'this section is read-only for you and no activation control is shown.',
  ask: 'An administrator of this deployment can grant it. Ask for the Offline Mode admin role on this organization.',
};

export const UNREACHABLE = {
  title: 'This console cannot reach the local server',
  reassurance:
    'Nothing has been changed. Whatever mode the organization was in, it is still in — the ' +
    'activation state lives on the local server, and this console has not been able to change it.',
  accessPath:
    'The local server is reachable only from the hospital network. It is not published to the ' +
    'internet, so a connection from outside the facility — including a VPN that does not carry ' +
    'the facility network — will not reach it. Get on the hospital network and retry.',
  escalation:
    'If you are already on the hospital network and this keeps failing, treat the local server ' +
    'itself as down and escalate to whoever operates it on site.',
  retryLabel: 'Retry',
  deploymentLabel: 'Local server',
};

export const WRITE_ERROR = {
  heading: 'The change was not made',
  retryLabel: 'Try again',
  dismissLabel: 'Dismiss',
  notSwitched: 'Offline Mode was not switched. Try again.',
};

export const DIVERGENCE_UNRESOLVED =
  'This server and the cloud hold different Offline Mode records. This will not resolve on its own.';

export type ReachBucketId = 'reachable_now' | 'reachable_on_rejoin' | 'not_reachable';

export const REACH_BUCKET_IDS: ReachBucketId[] = [
  'reachable_now',
  'reachable_on_rejoin',
  'not_reachable',
];

export const REACH = {
  heading: (direction: 'activate' | 'return'): string =>
    direction === 'activate'
      ? 'Who this will reach'
      : 'Who will learn that the organization is back',

  buckets: {
    reachable_now: { label: 'Reachable now' },
    reachable_on_rejoin: { label: 'Reachable when they rejoin' },
    not_reachable: { label: 'Not reachable' },
  } as Record<ReachBucketId, { label: string }>,

  bound: {
    at_least: 'At least',
    at_most: 'At most',
    exact: '',
  },

  notMeasured: 'Not measured',

  gathering: {
    label: 'Gathering the reach figures from the local server',
    note: 'The confirm button below does not wait for this.',
  },

  deadline: {
    heading: 'Reach cannot be measured right now',
    body: 'The local server has not answered with reach figures. If they arrive, they appear here.',
  },

  unmeasurable: {
    heading: 'Reach cannot be measured right now',
  },

  confirmStillAvailable: 'You can still confirm; plan to reach people another way.',

  lateArrival: 'These figures arrived after this panel stopped waiting for them.',

  channels: {
    availableNow: 'Available now',
    unknownName: 'Other channel',
    names: {
      local_push: 'Local push',
      push: 'Push notification',
      sms: 'SMS',
      email: 'Email',
      voice: 'Voice call',
      pager: 'Pager',
    } as Record<string, string>,
  },

  failure: {
    unreachable:
      'The local server did not answer when this panel asked who would be reached. This is not a count of nobody.',
    forbidden:
      'The local server refused this account the reach figures. Reading them needs the same Offline Mode permission as the activation itself.',
    'read-failed':
      'The local server could not read the records it counts from. That is not a measurement of nobody.',
    unusable:
      'The local server answered, but not with figures this console can present honestly, so none are shown.',
  } as Record<string, string>,
};

export const EVENT_LOG_TYPE_LABELS: Record<string, string> = {
  activation: 'Offline Mode activated',
  deactivation: 'Returned to Cloud Mode',
  reach_unavailable: 'Reach could not be measured at activation',
  broadcast: 'Broadcast sent',
  care_conduit_routing_change: 'Care Conduit routing changed',
  publication_status: 'Publication to TigerConnect',
};

export const EVENT_LOG = {
  heading: 'This Event',
  endedHeading: 'Last Event',

  unknownType: (eventType: string): string =>
    eventType ? `Recorded event: ${eventType}` : 'Recorded event',

  sentence: (label: string, actorName?: string): string =>
    actorName ? `${label} by ${actorName}` : label,

  gathering: {
    label: 'Reading the event log from the local server',
  },

  notNotified:
    'No broadcast has gone out this event. Devices show the banner, but nothing has interrupted ' +
    'anyone yet — use Send Broadcast Message to reach people now.',

  more: {
    label: 'Show older entries',
    inFlightLabel: 'Reading…',
    end: 'That is every entry this deployment still holds.',
  },

  notPermitted: {
    heading: 'You do not have permission to read the event log',
    body:
      'The event log is an audit record — it names administrators, times and message bodies — so ' +
      'reading it needs the offline_mode_activation permission on this deployment, separately from ' +
      'the activation state itself. Your account was refused.',
    reassurance:
      'Nothing is wrong with the log and nothing about Offline Mode has changed. This is the only ' +
      'part of this panel you cannot see.',
    ask: 'An administrator of this deployment can grant the Offline Mode admin role on this organization.',
  },

  failure: {
    heading: 'The event log could not be read',
    reasons: {
      unreachable:
        'The local server did not answer when this panel asked for the event log. This is not an ' +
        'empty log — this console could not read it.',
      'read-failed':
        'The local server could not read its event store. It reported a failure rather than an ' +
        'empty log, which is correct: a store that cannot be read is not a log with nothing in it.',
      unusable:
        'The local server answered, but not with an event log this console can read. Nothing is ' +
        'shown rather than a partial one.',
      forbidden:
        'The local server refused this account the event log. Reading it needs the same Offline ' +
        'Mode permission as the activation itself.',
    } as Record<string, string>,
    retryLabel: 'Try again',
    unreadableEntries: (unreadable: number): string =>
      unreadable === 1
        ? '1 entry on this page could not be read by this console and is not shown below.'
        : `${unreadable} entries on this page could not be read by this console and are not shown below.`,
  },

  integrity: {
    heading: 'This log failed its integrity check.',
    findings: {
      tail_missing: 'Older entries are missing.',
      seq_gap: 'Entries are missing from the sequence.',
      chain_break: 'An entry does not link to the one before it.',
      hash_mismatch: 'An entry was changed after it was written.',
      malformed: 'An entry could not be read.',
    } as Record<string, string>,
    unrecognised: 'An unrecognised problem was found.',
  },
};

export type BroadcastDirection = 'to_offline' | 'to_cloud';

const PHI_KIND: Record<string, string> = {
  ssn: 'a Social Security number',
  mrn: 'a medical record number',
  dob: 'a date of birth',
  long_digit_run: 'a long run of digits',
};

const REFUSAL_COPY: Record<OfflineModeBroadcastRefusalCode, (kind?: string) => string> = {
  empty_body: (): string =>
    'The message reached the server empty. Enter a message and send it again.',
  body_too_large: (): string =>
    'This message is longer than the server accepts. Shorten it and send it again.',
  invalid_template: (): string =>
    'The server did not recognise the template this was sent with. Pick a template again and resend.',
  no_recipients: (): string =>
    'There is nobody to send this to. This organization has no members on this deployment.',
  phi_suspected: (kind?: string): string =>
    `This looks like it contains ${
      (kind && PHI_KIND[kind]) || 'patient information'
    }. Remove it before sending.`,
  no_recorded_admin_action: (): string =>
    'The server could not tell which administrator is sending this. Sign in again, then resend.',
  forbidden: (): string => 'This account is not allowed to send a broadcast for this organization.',
  broadcast_rate_limited: (): string =>
    'Several broadcasts have already gone out for this organization in the last few minutes. Wait a moment and send it again.',
  roster_unavailable: (): string =>
    'The deployment could not read the member roster, so it did not send. Try again in a moment.',
  not_local_node: (): string =>
    'A broadcast can only be sent from the local deployment, and this console is not talking to it.',
  fanout_failed: (): string =>
    'The deployment failed to deliver to at least one person. Copies it already sent stand — check the event log before you resend.',
  unreachable: (): string =>
    'The local server did not answer. Whether the broadcast was sent is unknown — check the event log before you resend.',
  send_failed: (): string =>
    'The broadcast was not sent. The server reported a failure without saying more.',
};

const PREPARE_FAILURE_REASONS: Record<OfflineModeBroadcastPrepareErrorCode, string> = {
  unreachable:
    'The local server did not answer when this console asked for the broadcast options. Nothing ' +
    'has been sent.',
  forbidden:
    'The local server refused this account the broadcast options. Sending one needs the same ' +
    'Offline Mode permission as the activation itself.',
  'read-failed':
    'The local server could not read what it needs to prepare a broadcast. It reported a ' +
    'failure rather than an empty form, so nothing has been sent.',
  unusable:
    'The local server answered, but not with broadcast options this console can present. No ' +
    'message has been sent and nothing has been changed.',
};

export const BROADCAST = {
  title: (direction: BroadcastDirection, organizationName: string): string =>
    direction === 'to_cloud'
      ? `Send a return broadcast from ${organizationName}`
      : `Send a broadcast from ${organizationName}`,

  recipientsLine: (organizationName: string, count: string): string =>
    `Everyone in ${organizationName} — ${count} people`,

  reachableLine: (count: string): string =>
    `${count} devices can receive it right now; everyone else sees it when they next connect`,

  reachableUnknown: 'Live reach not measured',

  bodyLabel: 'Message',

  emptyBody: 'Enter a message to send.',

  zeroRecipients:
    'There is nobody to send this to. This organization has no members on this deployment.',

  send: 'Send Broadcast',
  sending: 'Sending…',
  cancel: 'Cancel',

  templateActivation: 'Activation notice',
  templateReturn: 'Return notice',

  replaceEditsPrompt: 'Replace your edits with the template?',
  replaceEditsConfirm: 'Replace',
  replaceEditsKeep: 'Keep my edits',

  phiKind: PHI_KIND,

  refusal: REFUSAL_COPY,

  gathering: 'Reading the broadcast options from the local server',

  loadFailure: {
    heading: 'The broadcast could not be prepared',
    reasons: PREPARE_FAILURE_REASONS,
    retryLabel: 'Try again',
    dismissLabel: 'Dismiss',
  },
};
